// @ts-strict
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Clef
//
// An object representing a Clef, converting line/space to note names,
// and vice-versa for a given Clef.
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import {
  clefNameToVexClefName,
  clefsCLines,
  isClefName,
} from 'shared/definitions/musicTheory/clefs';
import type { Accidental, NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import utils from '../utils';
import Interval from './interval';
import Note from './note';

export default class Clef {
  public clefName: ClefName;

  public middleC: number; // number of line for middle C

  public hidden: boolean = false;

  /**
   * @param {String} clefName default treble
   */
  constructor(clefOrClefName: ClefName | Clef = 'treble') {
    const { clefName, middleC } = parseClefName(clefOrClefName);
    this.clefName = clefName;
    this.middleC = middleC;
    return this;
  }

  setName(clefOrClefName: ClefName | Clef): Clef {
    const { clefName, middleC } = parseClefName(clefOrClefName);
    this.clefName = clefName;
    this.middleC = middleC;
    return this;
  }

  getName(): ClefName {
    return this.clefName;
  }

  getVexName(): string {
    return clefNameToVexClefName[this.clefName];
  }

  /**
   * @param {Number} line
   * @returns {Note}
   */
  getNoteAtLine(line: number): Note {
    // 1 line difference = +3 generic interval
    // 2 line difference = +5 generic interval
    // so: lineDif*2 + 1 converts to interval
    const lineDif = line - this.middleC;
    //      console.log("middleC: " + this.middleC);
    //      console.log("lineDif: " + lineDif);
    let genericInt = 2 * lineDif; // int from C up to note we want
    if (genericInt >= 0) genericInt++;
    // since we count intervals from 2nd's (not 1s)
    else genericInt--;
    const c4 = new Note('C4');
    return Interval.getNoteAtGenericInterval(c4, genericInt);
  }

  getLine(note: Note | NoteName): number {
    const genericInterval = Interval.getGenericInterval('C4', note);
    // genericInterval - 1 because a 2nd is only one note away
    const absDistance = genericInterval - Math.sign(genericInterval) * 1;
    const lineDif = absDistance / 2;
    return this.middleC + lineDif;
  }

  getVexLine(note: Note | NoteName): number {
    const line = this.getLine(note);
    const vexLine = 5 - line;
    return vexLine;
  }

  toInOrOnString(note: Note | NoteName): string {
    const line = this.getLine(note);
    if (line % 1 === 0) return 'on';
    return 'in';
  }

  toLineSpaceString(note: Note | NoteName): string {
    if (!(note instanceof Note) && !utils.isString(note)) {
      throw new Error('Clef can only give a toLineSpaceString from a Note or note string type.');
    }
    let line = this.getLine(note);
    let text = '';
    // if it's a line:
    if (line % 1 === 0) {
      // if it's a leger line above the staff:
      if (line > 5) {
        line -= 5;
        text += `${utils.ordinal(line)} ledger line above the staff`;
      } else if (line <= 0) {
        line--;
        line = -line;
        text += `${utils.ordinal(line)} ledger line below the staff`;
      } else {
        text += `${utils.ordinal(line)} line`;
      }
    } else {
      // if it is a space:
      line -= 0.5;
      // if it's a leger line above the staff:
      if (line > 4) {
        line -= 4;
        text += `${utils.ordinal(line)} space above the staff`;
      } else if (line <= 0) {
        line--;
        line = -line;
        text += `${utils.ordinal(line)} space below the staff`;
      } else {
        text += `${utils.ordinal(line)} space`;
      }
    }
    return text;
  }

  equals(clefOrName: Clef | string): boolean {
    if (clefOrName instanceof Clef) {
      return this.clefName === clefOrName.clefName;
    }
    return this.clefName === clefOrName;
  }

  isGreaterThan(clefOrName: Clef | ClefName): boolean {
    const clef = clefOrName instanceof Clef ? clefOrName : new Clef(clefOrName);
    return this.getNoteAtLine(0).isGreaterThan(clef.getNoteAtLine(0));
  }

  isLessThan(clefOrName: Clef | ClefName): boolean {
    const clef = clefOrName instanceof Clef ? clefOrName : new Clef(clefOrName);
    return this.getNoteAtLine(0).isLessThan(clef.getNoteAtLine(0));
  }

  toString(): ClefName {
    return this.clefName;
  }

  randomNote({
    accidentals,
    maxLedgerLines,
  }: { accidentals?: Accidental[]; maxLedgerLines?: number } = {}) {
    accidentals = accidentals || ['#', 'b', ''];
    maxLedgerLines = maxLedgerLines === undefined ? 2 : maxLedgerLines;
    const highest = this.getNoteAtLine(5 + maxLedgerLines);
    const lowest = this.getNoteAtLine(1 - maxLedgerLines);
    return Note.randomNote({ highest, lowest, accidentals });
  }

  static fromStringOrClef(stringOrClef: ClefName | Clef) {
    if (stringOrClef instanceof Clef) {
      return stringOrClef;
    }
    return new Clef(stringOrClef);
  }
}

function parseClefName(clefName: ClefName | Clef): { clefName: ClefName; middleC: number } {
  if (clefName instanceof Clef) {
    return { clefName: clefName.clefName, middleC: clefName.middleC };
  }

  clefName = clefName.toLowerCase() as ClefName;
  if (isClefName(clefName)) {
    return { clefName, middleC: clefsCLines[clefName] };
  }
  throw Error(`NaC: Invalid clef name: ${clefName}`);
}
